import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, ArrowLeft, Search } from 'lucide-react';

const getRoleClassName = (role) => {
  switch (role) {
    case 'admin':
      return 'bg-rose-100 text-rose-800 border border-rose-200';
    case 'manager':
      return 'bg-amber-100 text-amber-800 border border-amber-200';
    default:
      return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
  }
};

const UserManagementTable = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  
  const [users, setUsers] = useState([
    { id: 1, username: 'admin', email: 'admin@example.com', role: 'admin', token: 'TOKEN123456' },
    { id: 2, username: 'manager', email: 'manager@example.com', role: 'manager', token: 'TOKEN789012' },
    { id: 3, username: 'user1', email: 'user1@example.com', role: 'user', token: 'TOKEN345678' }
  ]);

  const handleAddUser = () => navigate('/userRegistration');
  const handleBackToAdminPanel = () => navigate('/adminUsersPanel');
  const handleEditUser = (user) => navigate('/userRegistration', { state: { user } });
  
  const handleDeleteUser = (userId) => {
    const confirmDelete = window.confirm('¿Estás seguro de que deseas eliminar este usuario?');
    if (confirmDelete) {
      setUsers(users.filter(user => user.id !== userId));
    }
  };

  const filteredUsers = users.filter(user => 
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="bg-white px-6 py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 md:mb-0">
                Gestión de Usuarios
              </h2>
              <div className="flex flex-col sm:flex-row gap-4">
                <button 
                  onClick={handleAddUser}
                  className="inline-flex items-center justify-center px-4 py-2 bg-stone-600 text-white rounded-lg hover:bg-stone-700 transition-all duration-200 font-medium shadow-sm"
                >
                  <UserPlus className="w-5 h-5 mr-2" />
                  Agregar Usuario
                </button>
                <button 
                  onClick={handleBackToAdminPanel}
                  className="inline-flex items-center justify-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-all duration-200 font-medium"
                >
                  <ArrowLeft className="w-5 h-5 mr-2" />
                  Volver al Panel
                </button>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por nombre, email o rol..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">ID</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Usuario</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Email</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Rol</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Token</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-6 py-4 text-sm text-gray-600">{user.id}</td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{user.username}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600">{user.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getRoleClassName(user.role)}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500 font-mono bg-gray-50 px-2 py-1 rounded">{user.token}</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center space-x-3">
                        <button 
                          onClick={() => handleEditUser(user)}
                          className="px-3 py-1 text-gray-900 hover:bg-gray-100 rounded transition-colors duration-200 text-sm"
                          title="Editar"
                        >
                          Editar
                        </button>
                        <button 
                          onClick={() => handleDeleteUser(user.id)}
                          className="px-3 py-1 text-gray-900 hover:bg-gray-100 rounded transition-colors duration-200 text-sm"
                          title="Eliminar"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredUsers.length === 0 && (
              <div className="text-center py-12">
                <div className="text-gray-400 text-lg">
                  {searchTerm ? 'No se encontraron usuarios que coincidan con la búsqueda' : 'No hay usuarios registrados'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagementTable;